import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { ShippingAddressInput, MagentoAvailableShippingMethod } from 'lib/types'
import { addressValidation } from 'lib/core/data/schema'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import { addressMatch } from 'lib/core/resources/helpers'
import { useCustomer, useDirectoryOptions } from 'lib/core/hooks'
import { Alert, Form, Grid, Spinner, Toast } from 'lib/ui'
import { GiftMessage, onlyNumbersInterceptor } from 'lib/shared'

import {
  useCheckout,
  useCheckoutContext,
  AddressPicker,
  ShippingMethodItem,
  ShippingStepActions,
  ShippingAddressFormValues as Values,
  shippingAddressFormSchema as schema,
  shippingAddressFormValidation as validation
} from 'lib/modules/checkout'

import { Container, ShippingMethodsContainer } from './styled'

export const ShippingStep = () => {
  const intl = useIntl()
  const { addresses, defaultShippingAddress } = useCustomer()
  const { setState } = useCheckoutContext()
  const {
    setShippingAddress,
    setShippingMethod,
    validateShippingAddress,
    shippingMethod,
    shippingAddress,
    hasShippingMethods,
    checkoutSchema
  } = useCheckout()

  const hasValidShippingAddress = validateShippingAddress()

  const address = checkoutSchema?.shippingAddress
  const availableShippingMethods = address?.availableShippingMethods

  const [addressId, setAddressId] = useState<number | undefined>()
  const [currentAddressId] = useState<number | undefined>(
    !addressId
      ? addresses?.find(element => addressMatch(element, address))?.id
      : undefined
  )
  const [defaultAddressId] = useState<number | undefined>(
    defaultShippingAddress && !addressId && !currentAddressId && !addressValidation().isValidSync(address)
      ? defaultShippingAddress.id
      : undefined
  )
  const [countryCode, setCountryCode] = useState<string>(address?.countryCode ? address.countryCode : DEFAULT_COUNTRY_CODE)

  const {
    countryInfoOptions,
    countryInfoIsFetching,
    postalCodeIsOptional,
    regionOptions,
    regionIsRequired
  } = useDirectoryOptions({ countryCode })

  const shippingMethodCode = address?.selectedShippingMethod?.method_code
  const [selectedShippingMethodCode, setSelectedShippingMethodCode] = useState(shippingMethodCode)
  const isLoading = shippingMethod.isFetching || shippingAddress.isFetching

  const handleCountryChange = ({ value, name, form }: any) => { // TODO: Type
    const { setFieldValue, setFieldTouched } = form
    setCountryCode(value)
    setFieldValue('regionCode', '', false)
    setFieldTouched('regionCode', false, false)
  }

  const handleShippingMethodChange = (method: MagentoAvailableShippingMethod) => {
    const { method_code: methodCode, carrier_code: carrierCode } = method
    setSelectedShippingMethodCode(methodCode)
    setShippingMethod({ carrierCode, methodCode }, {
      debounce: true,
      callback: {
        onFailure: () => {
          setSelectedShippingMethodCode(undefined)
          Toast.toastFailure({ messageId: 'checkout.shippingMethod.failure' })
        }
      }
    })
  }

  const handleShippingAddress = (values: Values) => {
    const {
      firstName,
      lastName,
      street,
      street2,
      city,
      regionCode,
      postalCode,
      countryCode,
      phoneNumber
    } = values

    const shippingAddress: ShippingAddressInput = {
      address: {
        city,
        countryCode,
        firstName,
        lastName,
        postalCode,
        regionCode,
        street,
        street2,
        phoneNumber
      }
    }

    setShippingAddress({ shippingAddress }, {
      debounce: true,
      callback: {
        onSuccess: () => {
          setState((state) => ({
            ...state,
            shippingErrors: {}
          }))
        },
        onFailure: () => {
          Toast.toastFailure({ messageId: 'checkout.shippingAddress.failure' })
        }
      }
    })
  }

  useEffect(() => {
    setSelectedShippingMethodCode(shippingMethodCode)
  }, [shippingMethodCode, setSelectedShippingMethodCode])

  useEffect(() => {
    if (currentAddressId) {
      setAddressId(currentAddressId)
    }
  }, [
    setAddressId,
    currentAddressId
  ])

  useEffect(() => {
    if (defaultAddressId) {
      setAddressId(defaultAddressId)
    }
  }, [
    setAddressId,
    defaultAddressId
  ])

  return (
    <Container>
      <Formik
        onSubmit={handleShippingAddress}
        validationSchema={validation({ intl, countryCode, postalCodeIsOptional, regionIsRequired })}
        initialValues={{
          ...schema,
          ...address,
          countryCode
        }}
      >
        <Form.Wrapper>
          <Form.FormikObserver<Values>
            autoSubmit
            onError={(errors) => {
              setState((state) => ({
                ...state,
                shippingErrors: errors
              }))
            }}
          />

          <h2>
            <FormattedMessage
              id='checkout.shipping.shippingInformationTitle'
            />
          </h2>

          <AddressPicker
            value={addressId}
            addresses={addresses}
            onChange={(values) => setAddressId(values?.id)}
            useAnotherLabel='Use another shipping address'
          />

          {!addressId && (
            <>
              <Grid>
                <div>
                  <Field
                    name='firstName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.firstName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='lastName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.lastName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Field
                name='phoneNumber'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.phoneNumber'
                })}
                component={Form.Input}
                interceptChange={onlyNumbersInterceptor}
                hasAutoPlaceholder
                isRequired
              />

              <Field
                name='street'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                isRequired
              />

              <Field
                name='street2'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street2'
                })}
                component={Form.Input}
                hasAutoPlaceholder
              />

              <Grid>
                <div>
                  <Field
                    name='city'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.city'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='regionCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.region'
                    })}
                    component={Form.Select}
                    options={regionOptions}
                    isLoading={countryInfoIsFetching}
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='postalCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.postalCode'
                    })}
                    component={Form.Input}
                    interceptChange={onlyNumbersInterceptor}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Field
                name='countryCode'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.country'
                })}
                component={Form.Select}
                options={countryInfoOptions}
                interceptChange={handleCountryChange}
                isLoading={countryInfoIsFetching}
                isRequired
              />
            </>
          )}

          <h2>
            Shipping Method {isLoading && <Spinner size={16} />}
          </h2>

          <ShippingMethodsContainer
            isLoading={isLoading}
          >
            {isLoading && !hasShippingMethods && (
              <Alert>
                <FormattedMessage id='app.loading' />
              </Alert>
            )}

            {!isLoading && !hasValidShippingAddress && !hasShippingMethods && (
              <Alert>
                Set your shipping information to see the available shipping methods
              </Alert>
            )}

            {!isLoading && hasValidShippingAddress && !hasShippingMethods && (
              <Alert>
                No shipping methods available for the current address
              </Alert>
            )}

            {availableShippingMethods?.map(method => {
              const { method_code: methodCode } = method
              const isSelected = methodCode === selectedShippingMethodCode
              return (
                <ShippingMethodItem
                  key={methodCode}
                  method={method}
                  isSelected={isSelected}
                  onChange={handleShippingMethodChange}
                />
              )
            })}
          </ShippingMethodsContainer>
        </Form.Wrapper>
      </Formik>
      <GiftMessage />
      <ShippingStepActions />
    </Container>
  )
}
